import { useId, useState } from 'react'
import toast from 'react-hot-toast'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { exportApi } from '../services/api'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
// Matches the backend's decoded-content limit (see POST /export/email) --
// checked here only to fail fast in the UI before spending a round trip.
const MAX_EMAIL_BYTES = 5 * 1024 * 1024

export interface ExportColumn<T> {
  key: string
  label: string
  /** Transforms the raw row value before exporting (e.g. format a date/currency). Falls back to String(row[key]). */
  format?: (row: T) => string
}

interface ExportBarProps<T> {
  data: T[]
  columns: ExportColumn<T>[]
  /** Without extension, e.g. "clientes" -> clientes.csv / clientes.pdf */
  filename: string
  /** Optional heading printed at the top of the generated PDF */
  title?: string
}

type EmailFormat = 'csv' | 'pdf'

function cellValue<T>(row: T, column: ExportColumn<T>): string {
  if (column.format) return column.format(row)
  const raw = (row as any)[column.key]
  return raw === null || raw === undefined ? '' : String(raw)
}

function escapeCsvValue(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function buildCsv<T>(data: T[], columns: ExportColumn<T>[]): string {
  const headerLine = columns.map((c) => escapeCsvValue(c.label)).join(',')
  const rowLines = data.map((row) => columns.map((c) => escapeCsvValue(cellValue(row, c))).join(','))
  return [headerLine, ...rowLines].map((line) => `${line}\r\n`).join('')
}

function buildPdf<T>(data: T[], columns: ExportColumn<T>[], title?: string): jsPDF {
  const doc = new jsPDF()
  if (title) {
    doc.setFontSize(14)
    doc.setTextColor('#43291b')
    doc.text(title, 14, 15)
  }
  autoTable(doc, {
    startY: title ? 22 : 14,
    head: [columns.map((c) => c.label)],
    body: data.map((row) => columns.map((c) => cellValue(row, c))),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [168, 72, 42] as [number, number, number] },
  })
  return doc
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = ''
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return btoa(binary)
}

export default function ExportBar<T>({ data, columns, filename, title }: ExportBarProps<T>) {
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [email, setEmail] = useState('')
  const [format, setFormat] = useState<EmailFormat>('csv')
  const [sending, setSending] = useState(false)
  const emailInputId = useId()
  const formatSelectId = useId()

  const isEmpty = data.length === 0
  const emptyTitle = isEmpty ? 'No hay datos para exportar' : undefined

  const handleDownloadCsv = () => {
    const csv = buildCsv(data, columns)
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${filename}.csv`)
  }

  const handleDownloadPdf = () => {
    const doc = buildPdf(data, columns, title)
    doc.save(`${filename}.pdf`)
  }

  const resetEmailForm = () => {
    setShowEmailForm(false)
    setEmail('')
    setFormat('csv')
  }

  const handleSendEmail = async () => {
    if (!EMAIL_RE.test(email.trim())) {
      toast.error('Ingresa un correo válido')
      return
    }

    let base64: string
    let mimeType: 'text/csv' | 'application/pdf'
    if (format === 'csv') {
      const csv = buildCsv(data, columns)
      base64 = uint8ArrayToBase64(new TextEncoder().encode(csv))
      mimeType = 'text/csv'
    } else {
      const doc = buildPdf(data, columns, title)
      base64 = uint8ArrayToBase64(new Uint8Array(doc.output('arraybuffer')))
      mimeType = 'application/pdf'
    }

    const approxBytes = Math.ceil((base64.length * 3) / 4)
    if (approxBytes > MAX_EMAIL_BYTES) {
      toast.error('El archivo generado supera el límite de 5 MB permitido para envío por correo')
      return
    }

    setSending(true)
    try {
      await exportApi.emailFile({
        recipient_email: email.trim(),
        subject: `Reporte: ${title || filename}`,
        filename: `${filename}.${format}`,
        content_base64: base64,
        mime_type: mimeType,
      })
      toast.success('Correo enviado')
      resetEmailForm()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al enviar el correo')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="mb-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={handleDownloadCsv}
          disabled={isEmpty}
          title={emptyTitle}
          className="px-3 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Descargar CSV
        </button>
        <button
          type="button"
          onClick={handleDownloadPdf}
          disabled={isEmpty}
          title={emptyTitle}
          className="px-3 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Descargar PDF
        </button>
        <button
          type="button"
          onClick={() => (showEmailForm ? resetEmailForm() : setShowEmailForm(true))}
          disabled={isEmpty}
          title={emptyTitle}
          aria-expanded={showEmailForm}
          className={`px-3 py-2 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
            showEmailForm
              ? 'border-terracotta-400 bg-terracotta-50 text-terracotta-700'
              : 'border-espresso-300 text-espresso-700 hover:bg-cream-100'
          }`}
        >
          Enviar por correo
        </button>
      </div>

      {showEmailForm && (
        <div className="mt-2 flex flex-wrap items-end gap-3 bg-cream-50 border border-espresso-200 rounded-md p-3">
          <div>
            <label htmlFor={emailInputId} className="block text-xs font-medium text-espresso-500 mb-1">
              Correo destinatario
            </label>
            <input
              id={emailInputId}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="correo@ejemplo.com"
              className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            />
          </div>
          <div>
            <label htmlFor={formatSelectId} className="block text-xs font-medium text-espresso-500 mb-1">
              Formato
            </label>
            <select
              id={formatSelectId}
              value={format}
              onChange={(e) => setFormat(e.target.value as EmailFormat)}
              className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
            >
              <option value="csv">CSV</option>
              <option value="pdf">PDF</option>
            </select>
          </div>
          <button
            type="button"
            onClick={handleSendEmail}
            disabled={sending || !email.trim()}
            className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
          >
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
          <button
            type="button"
            onClick={resetEmailForm}
            disabled={sending}
            className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100 disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      )}
    </div>
  )
}
